import useIsMounted from "../../../../../../hooks/use-is-mounted";
import React, {useCallback} from "react";
import {createDateRangeInitialValues, createDateRangeSchema, DateRangeFormSection, prepareDateRangeFilters} from "../form-sections/date-range";
import Api from "../../../../../../../core/services/api/api";
import DateUtils from "../../../../../../../core/services/date-utils";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {Card, Typography} from "@mui/material";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import Grid2 from "@mui/material/Unstable_Grid2";
import Button from "../../../../../../components/base/button/button";
import ExportingProgress from "../exporting-progress";

export const ExportDataViewInvoicesForm = ({fetchExportDataWithPagination, exportReport, exporting, progress}) => {
    const isMounted = useIsMounted();

    const schema = React.useMemo(() => {
        const dateRangeSchema = createDateRangeSchema();
        return Yup.object().shape({
            ...dateRangeSchema,
        })
    }, []);

    const initialValues = React.useMemo(() => {
        const dateRangeInitialValues = createDateRangeInitialValues();
        return {
            ...dateRangeInitialValues,
        }
    }, []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Fetches the data for export
     * @type {(function(*, *): Promise<{hasNext: boolean, data: *[]} | undefined>)}
     */
    const fetchAndPrepareData = useCallback(async (filters, pagination) => {
        const response = await Api.searchInvoices({
            filters: {
                fromDate: filters.startDate,
                ToDate: filters.endDate ?? DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
            },
            paginationInfo: pagination,
        });
        if (!isMounted())
            return;
        const length = (response.data?.paginationInfo?.length ?? 0);
        const hasNext = length > 0 && length > pagination.pageSize * pagination.currentPage;
        return {
            data: response.data?.resultList?.map((e, index) => ({
                "": (index + 1) + ((pagination.currentPage - 1) * pagination.pageSize),
                'Invoice No': `="${e.invoiceNo ?? ''}"`,
                'Customer': e.customer?.name ?? '',
                'Invoice Date': e.invoiceDate ? DateUtils.serverToLocal(e.invoiceDate).format(DateUtils.dateFormat) : '',
                'PO-Number': e.customerPO ?? '',
                'Sales Person': e.salespersonNo ?? '',
                'Avg Cost': formatMoney(e.avgCost ?? ''),
                'Discount': formatMoney(e.discountAmount ?? ''),
                'Applied Gift Card': formatMoney(e.appliedGiftCardAmount ?? ''),
                'Sub Total': formatMoney(e.subTotal ?? ''),
                'Total': formatMoney(e.total ?? ''),
                'Balance': formatMoney(e.payInfo?.minimumRequiredPayment ?? ''),
                'Status': e.status?.name ?? '',
            })) ?? [],
            hasNext,
            total: length,
        }
    }, [isMounted])

    /**
     * Exports the report based on the selected
     * @type {(function(*, *): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        const filters = {
            ...prepareDateRangeFilters(values),
        }
        await exportReport(async () => {
            const data = await fetchExportDataWithPagination(filters, fetchAndPrepareData, 100)
            return [data, "Invoices"];
        });
    }, [exportReport, fetchExportDataWithPagination, fetchAndPrepareData])


    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3, marginTop: 2}}>
                <Form
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{style: {width: '100%'}}}
                >
                    <Grid2 container spacing={3} justifyContent={'center'}>
                        <Grid2 xs={12} sx={{display: 'flex'}}>
                            <Typography variant={'subtitle1'} align={'center'}>
                                Select Date Range for Invoices Report
                            </Typography>
                        </Grid2>
                        <DateRangeFormSection required={required}/>
                        <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBlockStart: 2}}>
                            <Button size={"small"} disabled={exporting} variant={'outlined'} color={'primary'} type={'submit'} sx={{minWidth: 200}}>
                                {exporting ? "Exporting..." : "Export"}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Form>
                {
                    exporting &&
                    <ExportingProgress progress={progress}/>
                }
            </Card>
        </>
    );
}